import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RescType } from '@app/models/enums/resc-type.enum';
import { AuthResource } from '@app/models/settings/auth-resource';
import { treeToList } from '@core/utils';
import { ROUTER_UTILS } from '@core/utils/router.utils';
import { AuthService } from '@services/auth.service';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

const EXEMPT_SEGMENTS: string[] = [
  ROUTER_UTILS.config.auth.root,
  ROUTER_UTILS.business.incentive.root,
  ROUTER_UTILS.business.pdfViewer,
  ROUTER_UTILS.business.smb,
  ROUTER_UTILS.business.splitData,
  ROUTER_UTILS.config.user.root,
  ROUTER_UTILS.config.errorResponse.notFound,
].map(s => s.toLowerCase());

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  constructor(private router: Router, private authService: AuthService) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const urlPath = state.url.split('?')[0];
    const topSegment = urlPath.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';

    if (!topSegment || EXEMPT_SEGMENTS.includes(topSegment)) {
      return of(true);
    }

    return this.getPermittedPaths().pipe(
      map(permittedPaths => {
        if (permittedPaths.length === 0) {
          return true;
        }

        if (this.isPathPermitted(urlPath, permittedPaths)) {
          return true;
        }

        const dashboardPath = `/${ROUTER_UTILS.business.dashboard}`;
        if (this.isPathPermitted(dashboardPath, permittedPaths)) {
          return this.router.createUrlTree([dashboardPath]);
        }

        const firstPath = permittedPaths[0];
        if (firstPath) {
          return this.router.createUrlTree([firstPath]);
        }

        const { root, login } = ROUTER_UTILS.config.auth;
        return this.router.createUrlTree(['/', root, login]);
      }),
    );
  }

  /**
   * Returns true if targetPath equals any permitted path, or is a descendant of one.
   * e.g. target=/settings/download_mgmt/detail, permitted=/settings/download_mgmt → allowed
   *      target=/settings/users_mgmt, permitted=/settings/download_mgmt → blocked
   */
  private isPathPermitted(targetPath: string, permittedPaths: string[]): boolean {
    const normalize = (p: string) => '/' + p.replace(/^\//, '').replace(/\/$/, '').toLowerCase();
    const target = normalize(targetPath);
    return permittedPaths.some(p => {
      const permitted = normalize(p);
      return target === permitted || target.startsWith(permitted + '/');
    });
  }

  private getPermittedPaths(): Observable<string[]> {
    const cached = this.authService.navListSubject.getValue();
    if (cached.length > 0) {
      return of(this.extractPaths(cached));
    }
    return this.authService.getAuthRescList().pipe(
      take(1),
      map(resources => this.extractPaths(resources ?? [])),
    );
  }

  /** Flatten the tree and collect only leaf ROUTE paths (no ROUTE children). */
  private extractPaths(resources: AuthResource[]): string[] {
    return (treeToList(resources) as AuthResource[])
      .filter(r => r.type === RescType.ROUTE && !!r.path && !this.hasRouteChildren(r))
      .map(r => r.path as string);
  }

  private hasRouteChildren(r: AuthResource): boolean {
    return !!r.children?.length && r.children.some(c => c.type === RescType.ROUTE);
  }
}


